import { CheckCircle2, CircleDashed, XCircle } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useAuthStatus, useBrand, useDestroyAccount, usePiiStatus } from '../../api/queries.ts';
import type { DestroyDetail } from '../../api/types.ts';
import { Button } from '../../components/ui/button.tsx';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog.tsx';
import { EmptyNote, ErrorNote, Loading, errorMessage } from '../../components/ui/feedback.tsx';

/** Permanently deleting the account: what it removes, the steps, and the button. */
export function DeleteAccountSection() {
  const auth = useAuthStatus();
  const brand = useBrand();
  const status = usePiiStatus();
  const destroy = useDestroyAccount();
  const [open, setOpen] = useState(false);
  const [understood, setUnderstood] = useState(false);

  const brandName = brand.data?.name ?? 'the app';
  const email = auth.data?.email ?? '';
  // Hosted mailboxes are deleted with the account; for everyone else nothing was ever stored.
  const suffix = email.toLowerCase().endsWith('@bluemailx.com') ? '' : ' Your emails were never stored on our servers, so there is nothing else to remove.';

  const confirm = () => {
    setOpen(false);
    setUnderstood(false);
    destroy.mutate();
  };

  return (
    <div className="rounded-[1.25rem] shadow-[inset_0_0_0_1px_color-mix(in_oklab,var(--danger)_22%,var(--line))]">
      <div className="p-6 sm:p-8">
        <div className="min-w-0">
          <p className="max-w-[62ch] text-[0.95rem] text-ink">
            Removes <span className="font-semibold">{email || 'this account'}</span> and everything stored for it from {brandName} cloud,
            straight away and for good.{suffix}
          </p>

          <ol className="mt-6 flex flex-col gap-4">
            <Step n={1}>
              <strong className="font-semibold">Remove the account from the app on every device.</strong> In {brandName}: More, Various Account
              Settings, tap the account, Edit Account, Remove Account, then OK.
            </Step>
            <Step n={2}>
              <strong className="font-semibold">Delete it here</strong> and wait for the confirmation. Repeat for each account you want gone.
            </Step>
          </ol>

          <div className="mt-7">
            {status.isPending ? <Loading label="Checking the account…" /> : null}
            {status.isError ? <ErrorNote title="Could not check the account">{errorMessage(status.error)}</ErrorNote> : null}
            {status.data && !status.data.isPiiExist ? (
              <EmptyNote title="Nothing to delete">This account has no data stored on {brandName} cloud.</EmptyNote>
            ) : null}
            {status.data?.isPiiExist ? (
              <div className="flex flex-col gap-4">
                <Button variant="destructive" className="self-start" onClick={() => setOpen(true)} disabled={destroy.isPending} track="delete_account">
                  {destroy.isPending ? 'Deleting…' : 'Delete account permanently'}
                </Button>
                {destroy.isError ? <ErrorNote title="The account was not deleted. Try again.">{errorMessage(destroy.error)}</ErrorNote> : null}
                {destroy.isSuccess ? <DestroyResult details={destroy.data.detailsList ?? []} /> : null}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete {email}?</DialogTitle>
            <DialogDescription>Everything stored for this account on {brandName} cloud is removed. You cannot undo this.</DialogDescription>
          </DialogHeader>
          <label className="flex cursor-pointer items-center gap-3 rounded-xl bg-paper px-4 py-3 text-sm font-medium text-ink">
            <input
              type="checkbox"
              className="h-4 w-4 accent-[var(--danger)]"
              checked={understood}
              onChange={(event) => setUnderstood(event.target.checked)}
            />
            I understand this cannot be undone
          </label>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} track="delete_account_keep">
              Keep my account
            </Button>
            <Button variant="destructive" onClick={confirm} disabled={!understood} track="delete_account_confirm">
              Delete account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Step({ n, children }: { n: number; children: ReactNode }) {
  return (
    <li className="flex gap-4 text-[0.95rem] leading-relaxed text-ink">
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-paper font-display text-sm font-semibold text-ink">{n}</span>
      <p className="max-w-[62ch] pt-0.5">{children}</p>
    </li>
  );
}

function DestroyResult({ details }: { details: DestroyDetail[] }) {
  return (
    <div className="rounded-2xl bg-ok-soft p-5 text-sm text-ink">
      <p className="font-semibold text-ok">Your account is deleted</p>
      {details.length ? (
        <ul className="mt-3 space-y-1.5">
          {details.map((detail, index) => (
            <li key={String(detail.id ?? index)} className="flex items-center gap-2">
              <StatusIcon status={detail.status} />
              {detail.component ?? 'component'}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function StatusIcon({ status }: { status: DestroyDetail['status'] }) {
  if (status === 'REQUESTED') return <CircleDashed className="h-4 w-4 animate-spin text-muted" />;
  if (status === 'FAILED') return <XCircle className="h-4 w-4 text-danger" />;
  return <CheckCircle2 className="h-4 w-4 text-ok" />;
}
